import type { DocumentSettingsFormValues } from './types';
import { useCallback } from 'react';
import type { UpdateDocumentPayload } from 'services/documents/DocumentRepository';
import type { DocumentEntity } from 'services/documents/entities';
import { constructInitialValues } from './utils';
import useUpdateDocumentMutation from 'hooks/useUpdateDocumentMutation';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'utils/queryKeys';

import { isValidId } from '../../../../services/entities/MigrationEntities.ts';
import { ProspectType } from 'services/repositories/interfaces/ProspectRepository';

type Transformed = Omit<UpdateDocumentPayload, 'documentId'>;

function transformFormValues(changedValues: Partial<DocumentSettingsFormValues>, allValues: DocumentSettingsFormValues): Transformed {
  const transFormed: Transformed = {};

  if (changedValues.assignedUser !== undefined) {
    const value = changedValues.assignedUser?.value;
    transFormed.assignedUser = isValidId(value) ? { id: Number(value) } : null;
  }

  if (changedValues.contact !== undefined) {
    if (allValues.prospect) {
      const contact = changedValues.contact ? { id: String(changedValues.contact.value), name: '', email: '' } : undefined;
      transFormed.prospect = {
        id: allValues.prospect.id,
        type: allValues.prospect.type,
        contact,
      };
    } else if (changedValues.contact) {
      transFormed.prospect = {
        id: changedValues.contact.value,
        type: ProspectType.PERSON,
        contact: null,
      };
    } else {
      transFormed.prospect = null;
    }
  }
  if (changedValues.prospect !== undefined) {
    if (changedValues.prospect == null) {
      if (allValues.contact) {
        transFormed.prospect = { id: allValues.contact.value, type: ProspectType.PERSON, contact: null };
      } else {
        transFormed.prospect = null;
      }
    } else {
      const contact = allValues.contact ? { id: String(allValues.contact.value), name: '', email: '' } : null;
      transFormed.prospect = { id: changedValues.prospect.id, type: changedValues.prospect.type, contact };
    }
  }

  return transFormed;
}

export function useDocumentSettingsForm(document: DocumentEntity) {
  const initialValues = constructInitialValues(document);
  const { mutate } = useUpdateDocumentMutation();
  const queryClient = useQueryClient();

  const updateVariableValues = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [...QueryKeys.variables, document.contentId],
    });
    queryClient.invalidateQueries({
      queryKey: QueryKeys.editorAssignedCustomFields(document.contentId),
    });
    queryClient.invalidateQueries({
      queryKey: QueryKeys.getDocumentMetadataByContentId(document.contentId),
    });
  }, [queryClient, document.contentId]);

  const onValuesChange = useCallback(
    (changedValues: Partial<DocumentSettingsFormValues>, allValues: DocumentSettingsFormValues) => {
      const payload = transformFormValues(changedValues, allValues);
      mutate(
        { documentId: document.id, contentId: document.contentId, ...payload },
        {
          onSuccess: updateVariableValues,
        }
      );
    },
    [mutate, document.id, document.contentId, updateVariableValues]
  );

  return {
    initialValues,
    onValuesChange,
  };
}
